// exportUtils.js
// Utility for handling exports across the application

// Import dependencies
import { getItem as getItemOriginal } from './localStorageControl';
// Import notification directly to avoid ES module issues in tests
let notification;
try {
  // This will work in the app but might fail in tests
  const antd = require('ant-design-vue');
  notification = antd.notification;
} catch (e) {
  // Provide a fallback for tests
  notification = {
    error: () => {},
  };
}

/**
 * Generic function to handle data exports with different formats
 *
 * @param {Object} options - Configuration options
 * @param {string} options.endpoint - API endpoint for export
 * @param {Object} options.payload - Request payload
 * @param {string} options.fileType - File extension (e.g., 'xlsx', 'pdf')
 * @param {string} options.contentType - Content type for Accept header
 * @param {string} options.baseFileName - Base name for the file
 * @param {string} options.exportType - Type of export (e.g., 'self', 'verification')
 * @param {Function} options.onSuccess - Optional success callback
 * @param {Function} options.onError - Optional error callback
 * @param {Function} options.onFinally - Optional finally callback
 * @param {Function} options.axiosInstance - Axios instance to use
 * @param {Array} options.entityOptions - Array of entity options to find entity name
 * @returns {Promise} - The axios promise
 */
export function exportFile({
  endpoint,
  payload,
  fileType,
  contentType,
  baseFileName,
  exportType,
  onSuccess,
  onError,
  onFinally,
  axiosInstance,
  entityOptions,
  // For testing - allow dependency injection
  _getItem = getItemOriginal,
  // _notification = notification,
  _document = document,
  _window = window,
}) {
  const accessToken = _getItem('access_token');

  // Generate entity name for filename
  let entityName = baseFileName;
  if (entityOptions && Array.isArray(entityOptions)) {
    const foundEntity = entityOptions.find(e => e.value === payload.entitas_id);
    if (foundEntity) {
      entityName = foundEntity.text || baseFileName;
    }
  }

  // Generate timestamp for filename
  const date = new Date();
  const offset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() - offset);
  const timestamp = date
    .toISOString()
    .replace(/[-:T.]/g, '')
    .slice(0, 14);

  // Determine filename based on export type
  const filePrefix = exportType === 'self' ? 'realisasi_' : 'realisasi_verifikasi_';
  const fileName = `${filePrefix}${entityName}_${timestamp}.${fileType}`;

  return axiosInstance
    .post(endpoint, payload, {
      headers: {
        Accept: contentType,
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      responseType: 'blob',
    })
    .then(response => {
      // Create download link
      const blob = new Blob([response.data]);
      const url = _window.URL.createObjectURL(blob);
      const a = _document.createElement('a');

      a.href = url;
      a.download = fileName;
      _document.body.appendChild(a);
      a.click();

      // Clean up
      _window.URL.revokeObjectURL(url);
      _document.body.removeChild(a);

      if (onSuccess) onSuccess(response);
    })
    .catch(error => {
      console.error(`Error exporting ${fileType}:`, error);
      notification.error({
        message: 'Export failed',
        description: error.response?.data?.message || error.message || `Failed to export ${fileType}`,
      });

      if (onError) onError(error);
    })
    .finally(() => {
      if (onFinally) onFinally();
    });
}
